import React from 'react';
import { DateRow, EditWithIcon, IconAndTitle, SimpleTooltip, TextRow, TypeRow } from 'components';
import { Images, noteStatusEnums, PermissionList, RolePermission, User } from 'utils';
import { v4 as uuidv4 } from 'uuid';
import { FormStatusBox } from './fragments';
import { useSelector } from 'react-redux';

export const formBuilderStatusEnums = {
    PUBLISHED: 'PUBLISHED',
    UNPUBLISHED: 'UNPUBLISHED',
};

/** Mirrors `FormSearchKey` (package) — the `searchKey` `GET /form-builder` accepts. */
export const formSearchKeyEnums = {
    TITLE: 'TITLE',
};

export const formModelEnums = {
    STAFFNOTES: 'STAFF_NOTES',
    CLIENTNOTES: 'CLIENT_NOTES',
    PAYERNOTES: 'PAYER_NOTES',
    CLAIMNOTES: 'CLAIM_NOTES',
    INVOICENOTES: 'INVOICE_NOTES',
    TIMESHEETNOTE: 'TIMESHEET_NOTE',
    CLINICALNOTES: 'CLINICAL_NOTES',
    OFFICENOTES: 'OFFICE_NOTES',
    PROGRESSREPORT: 'PROGRESS_REPORT',
    GOALNOTES: 'GOAL_NOTES',
    INTAKEFORM: 'INTAKE_FORM',
    CLIENT_SECTIONS: 'CLIENT_SECTION',


    ABC_DATA: 'ABC_DATA',

    // FUNDERNOTES: 'FUNDER_NOTES',
    // ASSESSMENT: 'ASSESSMENT',
    // GOAL_DETAILS: 'GOAL_DETAIL',
};

/**
 * Note models whose resource IS an appointment.
 *
 * Both carry blocks the api resolves from that appointment rather than from the
 * form — SESSION_DATA_TABLE (session notes only) and auto-filling SIGNATURE
 * blocks (both) — so the appointment id has to travel with the template read, or
 * those blocks come back empty while the note is being filled.
 */
export const isApptNoteModel = (model) =>
    model === formModelEnums.CLINICALNOTES || model === formModelEnums.OFFICENOTES;

/**
 * The session-note template rules an appointment's service sets: the templates it links (the
 * first is the default) and whether staff may use only those. Services saved before
 * multi-template linking carry only `noteTemplateId`.
 */
export const getServiceTemplate = (service) => {
    const linked = service?.noteTemplateIds?.length ? service.noteTemplateIds : [service?.noteTemplateId];
    const templates = linked
        .filter(Boolean)
        .map((template) => ({ id: template?._id || template?.id || template, title: template?.title }));
    if (!templates.length) return null;
    return { templates, onlyUse: !!service?.onlyUseTemplate };
};

/** The template a new note opens with: the service's first published template, else the org default. */
export const pickInitialTemplate = (forms, serviceTemplate) => {
    const linked = (serviceTemplate?.templates || []).map((template) => forms?.find((form) => form?.id === template.id));
    return linked.find(Boolean) || (!serviceTemplate?.onlyUse ? forms?.find((form) => form?.default) : undefined);
};

/**
 * Template types and publish states offered by the two column filters.
 *
 * Exported rather than kept module-private so the SelectedFilters chip bar can label a selection
 * from the same lists the columns offered it from. Each column prepends its own `All` option; that
 * is a control, not a value, so it stays out of these.
 */
export const formModelsList = [
    { name: 'ABC Data', id: formModelEnums.ABC_DATA },
    { name: 'Staff Notes', id: formModelEnums.STAFFNOTES },
    { name: 'Client Notes', id: formModelEnums.CLIENTNOTES },
    { name: 'Payer Notes', id: formModelEnums.PAYERNOTES },
    { name: 'Claim Notes', id: formModelEnums.CLAIMNOTES },
    { name: 'Invoice Notes', id: formModelEnums.INVOICENOTES },
    { name: 'Timesheet Note', id: formModelEnums.TIMESHEETNOTE },
    // { name: 'Clinical Notes', id: formModelEnums.CLINICALNOTES },
    { name: 'Session Notes', id: formModelEnums.CLINICALNOTES },
    { name: 'Office Notes', id: formModelEnums.OFFICENOTES },
    { name: 'Progress Report', id: formModelEnums.PROGRESSREPORT },
    { name: 'Goal Notes', id: formModelEnums.GOALNOTES },
    { name: 'Client Section', id: formModelEnums.CLIENT_SECTIONS },
    { name: 'Intake Form', id: formModelEnums.INTAKEFORM },
];
const formatModelName = (inputs) => {
    if (!inputs) return;

    return inputs.map((input) =>
        input
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase())
    );
};

export const formStatusFilterList = [
    { name: 'Published', id: formBuilderStatusEnums.PUBLISHED },
    { name: 'Unpublished', id: formBuilderStatusEnums.UNPUBLISHED },
];

export const formHead = [
    {
        name: 'model',
        title: 'Template Type',
        disabled: true,
        filterList: [{ name: 'All', id: 'All' }, ...formModelsList],
    },
    { name: 'title', searchKey: formSearchKeyEnums.TITLE, title: 'Template Name', custom: false },
    { name: '', title: 'Created Date', custom: false },
    { name: '', title: 'Created By', custom: false },
    {
        name: 'status',
        title: 'Status',
        disabled: true,
        filterList: [{ name: 'All', id: 'All' }, ...formStatusFilterList],
    },
    RolePermission([PermissionList.MANAGE_CUSTOM_FORM?.code]) && { name: '', title: 'Action', custom: false, width: '150px' },
];

const renderFormIcons = (type) => {
    switch (type) {
        // case formModelEnums.CLIENTNOTES:
        //     return Images.formClientNotes;

        // case formModelEnums.PAYERNOTES:
        //     return Images.formFsNotes;

        // case formModelEnums.CLAIMNOTES:
        //     return Images.formClaimNotes;

        // case formModelEnums.TIMESHEETNOTE:
        //     return Images.formTimesheetNotes;

        // case formModelEnums.CLINICALNOTES:
        //     return Images.formClinicalNotes;

        case formModelEnums.PAYERNOTES:
            return Images.formInvoiceNotes;

        case formModelEnums.STAFFNOTES:
            return Images.formStaffNotes;

        case formModelEnums.ABC_DATA:
            return Images.formAbcNotes;

        case formModelEnums.CLINICALNOTES:
        case formModelEnums.PROGRESSREPORT:
        case formModelEnums.GOALNOTES:
            return Images.formClaimNotes;

        case formModelEnums.TIMESHEETNOTE:
            return Images.formClinicalNotes;

        case formModelEnums.INVOICENOTES:
        case formModelEnums.CLAIMNOTES:
            return Images.formInvoiceNotes;

        case formModelEnums.CLIENT_SECTIONS:
        case formModelEnums.CLIENTNOTES:
        case formModelEnums.OFFICENOTES:
        case formModelEnums.INTAKEFORM:
            return Images.formOfficeNotes;

        default:
            return Images.formTimesheetNotes;
        // return Images.formIcon;
    }
};

export const formBody = (selectedId) => [
    {
        rowText: (item) => (
            <div className="flex-align-center" style={{ gap: 8 }}>
                <img src={renderFormIcons(item?.models?.length && item?.models[0])} alt="formIcon" />
                <p>{item?.default ? 'All' : formatModelName(item?.models)}</p>
                {item?.default && (
                    <SimpleTooltip title={<p>This is a default system template and cannot be modified</p>} placement="bottom" arrow>
                        <img src={Images.blockedForm} alt="flag" />
                    </SimpleTooltip>
                )}
            </div>
        ),
    },

    {
        rowText: (item) => (
            <div className="flex-align-center" style={{ gap: 8 }}>
                <TextRow name={item?.title} />
            </div>
        ),
    },
    { rowText: (item) => <DateRow date={item?.createdAt} /> },
    { rowText: (item) => <TextRow name={item?.default ? 'System' : item?.createdBy} /> },

    {
        rowText: (item) => {
            return (
               <FormStatusBox info={item}  />
            );
        },
    },

    RolePermission([PermissionList.MANAGE_CUSTOM_FORM?.code]) && {
        button: (fn, item) => (
            <div className="flex-align-center" style={{ gap: 8 }}>
                    <EditWithIcon
                        load={selectedId === item?.id}
                        id={'duplicate-form'}
                        icon={Images.duplicate}
                        type={'button'}
                        handleClick={(e) => {
                            fn('duplicate');
                            e.stopPropagation();
                        }}
                    />
                {!item?.default && (
                    <EditWithIcon
                        id={'edit-form'}
                        icon={Images.editAuth}
                        type={'button'}
                        handleClick={(e) => {
                            fn('edit');
                            e.stopPropagation();
                        }}
                    />
                )}
                {!item?.default && (
                    <EditWithIcon
                        id={'delete-form'}
                        icon={Images.deleteTag}
                        type={'button'}
                        handleClick={(e) => {
                            fn('delete');
                            e.stopPropagation();
                        }}
                    />
                )}
            </div>
        ),
    },
];

export const ACTION_TYPE = 'GET_FORMS';
export const DELETE_ACTION_TYPE = 'GET_FORMS';

export const formNotYet = {
    title: 'No Templates Added Yet',
    subTitle: 'Start adding your custom templates.',
    image: Images.noFsYet,
};

export const FORM_SETTINGS = 'GET_FORM_SETTINGS';
export const SINGLE_FORM = 'GET_FORM_BY_ID';
export const GET_PROGRESS_REPORT = 'GET_PROGRESS_REPORTS_BY_ID';
export const DISCARD_ACTION_TYPE = 'DISCARD_DOCUMENT_DRAFT';
/** Success key the shared toast channel maps to a message (utils/hooks/toastSuccess.js). */
export const AI_FORM_GENERATED_TOAST = 'AI_FORM_GENERATED';

/**
 * Kind of AI auto-fill that a RICH_TEXT attribute is wired to. Stored on
 * `attribute.properties.aiSummary` (replaces the old boolean) so the marker
 * is a stable, future-extensible identifier rather than just true/false.
 */
export const AiSummaryKind = {
    SESSION_SUMMARY: 'SESSION_SUMMARY',
};

export const formAttributeEnums = {
    NAMEINPUT: 'NAMEINPUT',
    PHONEINPUT: 'PHONEINPUT',
    EMAILINPUT: 'EMAILINPUT',
    LONGTEXT: 'LONGTEXT',
    INSTRUCTIONS: 'INSTRUCTIONS',
    SINGLELINETEXT: 'SINGLELINETEXT',
    ADDRESSINPUT: 'ADDRESSINPUT',
    DATEPICKER: 'DATEPICKER',
    MULTIPLECHOICE: 'MULTIPLECHOICE',
    SINGLECHOICE: 'SINGLECHOICE',
    TIMEINPUT: 'TIMEINPUT',
    SIGNATURE: 'SIGNATURE',
    UPLOADIMAGE: 'UPLOADIMAGE',
    PARAGRAPH: 'PARAGRAPH',

    MULTIPLESELECT: 'MULTIPLESELECT',
    SINGLESELECT: 'SINGLESELECT',

    AVAILABILITY: 'AVAILABILITY',
    PAGEBREAK: 'PAGEBREAK',
    DIVIDER: 'DIVIDER',

    GOAL: 'GOAL',
    ABC_DATA: 'ABC_DATA',
    EVENT: 'EVENT',
    ASSESSMENT: 'ASSESSMENT',
};

// export const renderFormIcon = (name) => {
//     switch (name) {
//         case formAttributeEnums.NAMEINPUT:
//             return Images.fName;
//
//         case formAttributeEnums.PHONEINPUT:
//             return Images.fPhone;
//
//         case formAttributeEnums.EMAILINPUT:
//             return Images.fEmail;
//
//         case formAttributeEnums.ADDRESSINPUT:
//             return Images.fAddress;
//
//         case formAttributeEnums.SIGNATURE:
//             return Images.fSignature;
//
//         case formAttributeEnums.UPLOADIMAGE:
//             return Images.fUpload;
//
//         case formAttributeEnums.INSTRUCTIONS:
//             return Images.fHeading;
//
//         case formAttributeEnums.LONGTEXT:
//             return Images.fLongText;
//
//         case formAttributeEnums.SINGLELINETEXT:
//             return Images.fSingleLine;
//
//         case formAttributeEnums.AVAILABILITY:
//             return Images.fAvailability;
//
//         case formAttributeEnums.DATEPICKER:
//             return Images.fDatePicker;
//
//         case formAttributeEnums.MULTIPLECHOICE:
//             return Images.fMultipleChoice;
//
//         case formAttributeEnums.SINGLECHOICE:
//             return Images.fMultipleChoice;
//
//         case formAttributeEnums.SINGLESELECT:
//             return Images.fMultipleChoice;
//
//         case formAttributeEnums.MULTIPLESELECT:
//             return Images.fMultipleChoice;
//
//         case formAttributeEnums.DIVIDER:
//             return Images.fDivider;
//
//         case formAttributeEnums.PAGEBREAK:
//             return Images.fPageBreak;
//
//         case formAttributeEnums.PARAGRAPH:
//             return Images.fLongText;
//
//         case formAttributeEnums.GOAL:
//             return Images.fGoal;
//
//         default:
//             return Images.fName;
//     }
// };

export const getWidth = (w) => {
    switch (w) {
        case '1/2':
            return 'calc(50% - 6px)';
        case '1/3':
            return 'calc(33.3333% - 8px)';
        case '1/4':
            return 'calc(25% - 9px)';
        default:
            return '100%';
    }
};

export const clientInviteSynchronizationKey = {
    CLIENT_FIRST_NAME: 'CLIENT_FIRST_NAME',
    CLIENT_LAST_NAME: 'CLIENT_LAST_NAME',
    CLIENT_MIDDLE_NAME: 'CLIENT_MIDDLE_NAME',
    CLIENT_EMAIL: 'CLIENT_EMAIL',
    CLIENT_PHONE: 'CLIENT_PHONE',
    CLIENT_GENDER: 'CLIENT_GENDER',
    ETHNICITY: 'ETHNICITY',
    CLIENT_LANGUAGES: 'CLIENT_LANGUAGES',
    CLIENT_FAMILY_LANGUAGE: 'CLIENT_FAMILY_LANGUAGE',
    CLIENT_ADDRESS: 'CLIENT_ADDRESS',
    CONTACT_ADDRESS: 'CONTACT_ADDRESS',
    CONTACT_PHONE: 'CONTACT_PHONE',
    CONTACT_EMAIL: 'CONTACT_EMAIL',
    CONTACT_RELATIONSHIP: 'CONTACT_RELATIONSHIP',
    DEFAULT_SIGNER: 'DEFAULT_SIGNER',
    CONTACT_FIRST_NAME: 'CONTACT_FIRST_NAME',
    CONTACT_MIDDLE_NAME: 'CONTACT_MIDDLE_NAME',
    CLIENT_DATE_OF_BIRTH: 'CLIENT_DATE_OF_BIRTH',
};

const dateTypesList = [
    {
        name: 'Number',
        type: 'number',
        id: 'number',
    },
    {
        name: 'Phone',
        type: 'phone',
        id: 'phone',
    },
    {
        name: 'Email',
        type: 'email',
        id: 'email',
    },
    {
        name: 'Text',
        type: 'text',
        id: 'text',
    },
    {
        name: 'Date',
        type: 'date',
        id: 'date',
    },
    {
        name: 'Time',
        type: 'time',
        id: 'time',
    },
];

export const IntakeForm = [
    {
        id: uuidv4(),
        name: 'Client Information',
        attributes: [
            {
                label: 'First Name',
                name: 'input',
                type: 'INPUT',
                id: 'd53f8c93-b835-452b-be61-90c465f310b4',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_FIRST_NAME',
                    updateForbidden: true,
                },
            },
            {
                label: 'Last Name',
                name: 'input',
                type: 'INPUT',
                id: 'f4bfee4e-6797-47dd-b2a1-ffa05b186c64',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_LAST_NAME',
                    updateForbidden: true,
                },
            },
            {
                label: 'Middle Name',
                name: 'input',
                type: 'INPUT',
                id: '68577f2f-961f-4bc4-8db9-c4472e308ac3',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_MIDDLE_NAME',
                    updateForbidden: true,
                },
            },
            {
                label: 'Email Address',
                name: 'input',
                type: 'INPUT',
                id: 'e6a43855-53b3-4a83-95c3-043363af1b9d',
                defaultDateType: 'email',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_EMAIL',
                    updateForbidden: true,
                },
            },
            {
                label: 'Phone Number',
                name: 'input',
                type: 'INPUT',
                id: 'fb14106c-63eb-4630-aa23-4e1ca2afb848',
                defaultDateType: 'phone',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_PHONE',
                    updateForbidden: true,
                },
            },
            {
                label: 'Gender',
                name: 'radio',
                type: 'RADIO_BUTTON',
                id: '770aa9ba-9e94-493c-a684-6d0e6f2d893f',
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_GENDER',
                    updateForbidden: true,
                    options: [
                        {
                            id: uuidv4(),
                            name: 'Male',
                            value: 'Male',
                        },
                        {
                            id: uuidv4(),
                            name: 'Female',
                            value: 'Female',
                        },
                        {
                            id: uuidv4(),
                            name: 'Other',
                            value: 'Other',
                        },
                    ],
                },
            },
            {
                label: 'Date of Birth',
                name: 'input',
                type: 'INPUT',
                id: '0182ff3b-dc0f-4c5b-aca9-3e275ac39dd4',
                defaultDateType: 'date',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_DATE_OF_BIRTH',
                    updateForbidden: true,
                },
            },
            {
                label: 'Ethnicity',
                name: 'input',
                type: 'INPUT',
                id: '96ecfa67-1e49-4a23-9685-a819199d788a',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'ETHNICITY',
                    updateForbidden: true,
                },
            },
            {
                label: 'Language',
                name: 'select',
                type: 'SELECT',
                id: 'd2c213f2-0bdc-4824-a545-e608625df43f',
                border: true,
                defaultDateType: 'MULTIPLE',
                dateTypesList: [
                    {
                        name: 'Single Select',
                        type: 'SINGLE',
                        id: 'SINGLE',
                    },
                    {
                        name: 'Multiple Select',
                        type: 'MULTIPLE',
                        id: 'MULTIPLE',
                    },
                ],
                properties: {
                    updateForbidden: true,
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_LANGUAGES',
                    options: [
                        {
                            id: 'ALBANIAN',
                            name: 'Albanian',
                            value: 'Albanian',
                        },
                        {
                            id: 'ARMENIAN',
                            name: 'Armenian',
                            value: 'Armenian',
                        },
                        {
                            id: 'BULGARIAN',
                            name: 'Bulgarian',
                            value: 'Bulgarian',
                        },
                        {
                            id: 'CATALAN',
                            name: 'Catalan',
                            value: 'Catalan',
                        },
                        {
                            id: 'CHINESE',
                            name: 'Chinese',
                            value: 'Chinese',
                        },
                        {
                            id: 'CROATIAN',
                            name: 'Croatian',
                            value: 'Croatian',
                        },
                        {
                            id: 'CZECH',
                            name: 'Czech',
                            value: 'Czech',
                        },
                        {
                            id: 'DANISH',
                            name: 'Danish',
                            value: 'Danish',
                        },
                        {
                            id: 'GERMAN',
                            name: 'German',
                            value: 'German',
                        },
                        {
                            id: 'GREEK',
                            name: 'Greek',
                            value: 'Greek',
                        },
                        {
                            id: 'ENGLISH',
                            name: 'English',
                            value: 'English',
                        },
                        {
                            id: 'SPANISH',
                            name: 'Spanish',
                            value: 'Spanish',
                        },
                        {
                            id: 'FINNISH',
                            name: 'Finnish',
                            value: 'Finnish',
                        },
                        {
                            id: 'FRENCH',
                            name: 'French',
                            value: 'French',
                        },
                        {
                            id: 'HEBREW',
                            name: 'Hebrew',
                            value: 'Hebrew',
                        },
                        {
                            id: 'HUNGARIAN',
                            name: 'Hungarian',
                            value: 'Hungarian',
                        },
                        {
                            id: 'ICELANDIC',
                            name: 'Icelandic',
                            value: 'Icelandic',
                        },
                        {
                            id: 'ITALIAN',
                            name: 'Italian',
                            value: 'Italian',
                        },
                        {
                            id: 'INDONESIAN',
                            name: 'Indonesian',
                            value: 'Indonesian',
                        },
                        {
                            id: 'JAPANESE',
                            name: 'Japanese',
                            value: 'Japanese',
                        },
                        {
                            id: 'KOREAN',
                            name: 'Korean',
                            value: 'Korean',
                        },
                        {
                            id: 'DUTCH',
                            name: 'Dutch',
                            value: 'Dutch',
                        },
                        {
                            id: 'NORWEGIAN',
                            name: 'Norwegian',
                            value: 'Norwegian',
                        },
                        {
                            id: 'POLISH',
                            name: 'Polish',
                            value: 'Polish',
                        },
                        {
                            id: 'PORTUGUESE',
                            name: 'Portuguese',
                            value: 'Portuguese',
                        },
                        {
                            id: 'ROMANSH',
                            name: 'Romansh',
                            value: 'Romansh',
                        },
                        {
                            id: 'ROMANIAN',
                            name: 'Romanian',
                            value: 'Romanian',
                        },
                        {
                            id: 'RUSSIAN',
                            name: 'Russian',
                            value: 'Russian',
                        },
                        {
                            id: 'SLOVAK',
                            name: 'Slovak',
                            value: 'Slovak',
                        },
                        {
                            id: 'SWEDISH',
                            name: 'Swedish',
                            value: 'Swedish',
                        },
                        {
                            id: 'THAI',
                            name: 'Thai',
                            value: 'Thai',
                        },
                        {
                            id: 'TURKISH',
                            name: 'Turkish',
                            value: 'Turkish',
                        },
                        {
                            id: 'URDU',
                            name: 'Urdu',
                            value: 'Urdu',
                        },
                    ],
                },
            },
            {
                label: 'Family Language',
                name: 'select',
                type: 'SELECT',
                id: '6328ac30-812b-4999-a201-16201ef847be',
                border: true,
                defaultDateType: 'SINGLE',
                dateTypesList: [
                    {
                        name: 'Single Select',
                        type: 'SINGLE',
                        id: 'SINGLE',
                    },
                    {
                        name: 'Multiple Select',
                        type: 'MULTIPLE',
                        id: 'MULTIPLE',
                    },
                ],
                properties: {
                    updateForbidden: true,
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_FAMILY_LANGUAGE',
                    options: [
                        {
                            id: 'ALBANIAN',
                            name: 'Albanian',
                            value: 'Albanian',
                        },
                        {
                            id: 'ARMENIAN',
                            name: 'Armenian',
                            value: 'Armenian',
                        },
                        {
                            id: 'BULGARIAN',
                            name: 'Bulgarian',
                            value: 'Bulgarian',
                        },
                        {
                            id: 'CATALAN',
                            name: 'Catalan',
                            value: 'Catalan',
                        },
                        {
                            id: 'CHINESE',
                            name: 'Chinese',
                            value: 'Chinese',
                        },
                        {
                            id: 'CROATIAN',
                            name: 'Croatian',
                            value: 'Croatian',
                        },
                        {
                            id: 'CZECH',
                            name: 'Czech',
                            value: 'Czech',
                        },
                        {
                            id: 'DANISH',
                            name: 'Danish',
                            value: 'Danish',
                        },
                        {
                            id: 'GERMAN',
                            name: 'German',
                            value: 'German',
                        },
                        {
                            id: 'GREEK',
                            name: 'Greek',
                            value: 'Greek',
                        },
                        {
                            id: 'ENGLISH',
                            name: 'English',
                            value: 'English',
                        },
                        {
                            id: 'SPANISH',
                            name: 'Spanish',
                            value: 'Spanish',
                        },
                        {
                            id: 'FINNISH',
                            name: 'Finnish',
                            value: 'Finnish',
                        },
                        {
                            id: 'FRENCH',
                            name: 'French',
                            value: 'French',
                        },
                        {
                            id: 'HEBREW',
                            name: 'Hebrew',
                            value: 'Hebrew',
                        },
                        {
                            id: 'HUNGARIAN',
                            name: 'Hungarian',
                            value: 'Hungarian',
                        },
                        {
                            id: 'ICELANDIC',
                            name: 'Icelandic',
                            value: 'Icelandic',
                        },
                        {
                            id: 'ITALIAN',
                            name: 'Italian',
                            value: 'Italian',
                        },
                        {
                            id: 'INDONESIAN',
                            name: 'Indonesian',
                            value: 'Indonesian',
                        },
                        {
                            id: 'JAPANESE',
                            name: 'Japanese',
                            value: 'Japanese',
                        },
                        {
                            id: 'KOREAN',
                            name: 'Korean',
                            value: 'Korean',
                        },
                        {
                            id: 'DUTCH',
                            name: 'Dutch',
                            value: 'Dutch',
                        },
                        {
                            id: 'NORWEGIAN',
                            name: 'Norwegian',
                            value: 'Norwegian',
                        },
                        {
                            id: 'POLISH',
                            name: 'Polish',
                            value: 'Polish',
                        },
                        {
                            id: 'PORTUGUESE',
                            name: 'Portuguese',
                            value: 'Portuguese',
                        },
                        {
                            id: 'ROMANSH',
                            name: 'Romansh',
                            value: 'Romansh',
                        },
                        {
                            id: 'ROMANIAN',
                            name: 'Romanian',
                            value: 'Romanian',
                        },
                        {
                            id: 'RUSSIAN',
                            name: 'Russian',
                            value: 'Russian',
                        },
                        {
                            id: 'SLOVAK',
                            name: 'Slovak',
                            value: 'Slovak',
                        },
                        {
                            id: 'SWEDISH',
                            name: 'Swedish',
                            value: 'Swedish',
                        },
                        {
                            id: 'THAI',
                            name: 'Thai',
                            value: 'Thai',
                        },
                        {
                            id: 'TURKISH',
                            name: 'Turkish',
                            value: 'Turkish',
                        },
                        {
                            id: 'URDU',
                            name: 'Urdu',
                            value: 'Urdu',
                        },
                    ],
                },
            },
            {
                label: 'Client Address',
                name: 'input',
                type: 'INPUT',
                id: '78f1758d-e491-4baf-bfe3-84a42ec1e88b',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    updateForbidden: true,
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CLIENT_ADDRESS',
                },
            },
            {
                label: 'Guardian First Name',
                name: 'input',
                type: 'INPUT',
                id: 'af65ce4c-4a93-4c67-99f4-f3387f2118d6',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CONTACT_FIRST_NAME',
                    updateForbidden: true,
                },
            },
            {
                label: 'Guardian Last Name',
                name: 'input',
                type: 'INPUT',
                id: '4c8d9e8d-a5d8-45c8-9db4-2f23ccc0a84a',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CONTACT_MIDDLE_NAME',
                    updateForbidden: true,
                },
            },
            {
                label: 'Guardian Phone Number',
                name: 'input',
                type: 'INPUT',
                id: 'f51e24b8-878c-4725-9fc2-04ed39d774ec',
                defaultDateType: 'phone',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CONTACT_PHONE',
                    updateForbidden: true,
                },
            },
            {
                label: 'Guardian Email Address',
                name: 'input',
                type: 'INPUT',
                id: '621f9ac7-c4c9-49f6-8df7-b5d0fbf5f9c3',
                defaultDateType: 'email',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CONTACT_EMAIL',
                    updateForbidden: true,
                },
            },
            {
                label: 'Relationship',
                name: 'input',
                type: 'INPUT',
                id: 'a06febd1-192f-4cec-bb53-cf1942578c01',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CONTACT_RELATIONSHIP',
                    updateForbidden: true,
                },
            },
            {
                label: 'Guardian Address',
                name: 'input',
                type: 'INPUT',
                id: '818139d6-6cd4-47b5-9e46-fb7be315eaeb',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: true,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'CONTACT_ADDRESS',
                    updateForbidden: true,
                },
            },

            {
                label: 'Default Signer',
                name: 'input',
                type: 'INPUT',
                id: '6bd8892f-255d-41a5-8b21-fa9bfdbe923c',
                defaultDateType: 'text',
                dateTypesList: dateTypesList,
                properties: {
                    required: false,
                    readOnly: false,
                    width: 'Full',
                    synchronizationKey: 'DEFAULT_SIGNER',
                    updateForbidden: true,
                },
            },
        ],
    },
];


const firmStatusEnums = {
    PUBLISHED: 'PUBLISHED',
    UNPUBLISHED: 'UNPUBLISHED',
}


export const useNoteEditPermissions = (info, appt) => {
    const formById = useSelector((state) => state.forms.form);
    const { userInfo } = User();
    const checkSuperVizer = appt?.authorizedService?.renderingProvider?._id === userInfo?.id ? true : RolePermission([PermissionList.APPT_UPDATE_NOTES?.code]);
    const isUpdateRequest = info?.id && info?.status === noteStatusEnums.UPDATE_REQUEST;
    const isLocked =  info?.template?.formSettings?.lockNoteAfterSubmit || formById?.formSettings?.lockNoteAfterSubmit;
    const needApproved = info?.template?.formSettings?.needApproved || formById?.formSettings?.needApproved;

    // canEdit is poorly named: TRUE = read-only (UI shows only Close button); FALSE = editable.
    // Supervisor (renderingProvider OR APPT_UPDATE_NOTES) can always edit. Regular staff are gated by status + creator.
    const isCreator = info?.id ? userInfo?.id === info?.creatorId : true;
    const isStaff = !checkSuperVizer;
    let canEdit = false;
    if (info?.id && isStaff) {
        if (info?.status === noteStatusEnums.IN_REVIEW) {
            canEdit = true;
        } else if (info?.status === noteStatusEnums.COMPLETED) {
            if (isLocked || !isCreator) canEdit = true;
        } else if (info?.status === noteStatusEnums.DRAFT && !isCreator) {
            canEdit = true;
        } else if (info?.status === noteStatusEnums.UPDATE_REQUEST && !isCreator) {
            canEdit = true;
        }
    }

    return {
        canEdit: canEdit,
        checkSuperVizer: checkSuperVizer,
        isUpdateRequest: isUpdateRequest,
        isLocked: isLocked,
        needApproved: needApproved,
        isCreator: isCreator,
    }
}